import React, { useState, useEffect, useRef } from "react";
import {
  Heart,
  X,
  MapPin,
  DollarSign,
  Instagram,
  Facebook,
  Twitter,
  Clock,
  Globe,
  ExternalLink,
  Eye,
} from "lucide-react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import {
  API_BASE_URL,
  authenticatedFetch,
  getAuthenticatedWSUrl,
} from "../api.js";
import FoodieCallAltLogo from "../media/foodie-call-alt.png";
import RestaurantAltImage from "../media/restaurant-alt-image.png";
import SadPizzaImage from "../media/sad-pizza.png";
import HangTightPizza from "../media/hang-tight-pizza.png";
import { usePostHog } from "../Posthogprovider.jsx";

/* ---------------------- CONFIG ---------------------- */

const FOURSQUARE_API_KEY = process.env.REACT_APP_FOURSQUARE_API_KEY;
const FOURSQUARE_BASE_URL = process.env.REACT_APP_FOURSQUARE_BASE_URL;
const MAX_RESTAURANTS = 500;

// CRITICAL: Polling interval to check for matches even if WebSocket fails
const MATCH_POLL_INTERVAL = 3000; // Check every 3 seconds
const MATCH_POLL_TIMEOUT = 300000; // Stop polling after 5 minutes

const FOURSQUARE_FIELDS = [
  "fsq_id",
  "name",
  "location",
  "categories",
  "distance",
  "description",
  "tel",
  "website",
  "email",
  "social_media",
  "hours",
  "hours_popular",
  "rating",
  "stats",
  "popularity",
  "price",
  "menu",
  "photos",
  "features",
].join(",");

/* ---------------------- COOKIE HELPERS ---------------------- */

const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/* ---------------------- STYLED COMPONENTS ---------------------- */

const Header = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #FB923C, #F97373)",
  color: theme.palette.common.white,
  padding: theme.spacing(2.25, 3),
}));

const DemoBanner = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #fef3c7, #fde68a)",
  borderBottom: "2px solid #fbbf24",
  padding: theme.spacing(1.5, 3),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
}));

const DemoBannerText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.85rem",
  color: "#92400e",
  letterSpacing: "0.05em",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const DemoHint = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 140,
  left: "50%",
  transform: "translateX(-50%)",
  maxWidth: 320,
  width: "calc(100% - 48px)",
  backgroundColor: "#eff6ff",
  border: "2px solid #3b82f6",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2, 2.5),
  boxShadow: "0 8px 24px rgba(59, 130, 246, 0.3)",
  zIndex: theme.zIndex.appBar + 1,
  cursor: "pointer",
  transition: "all 0.2s ease",
  animation: "bounce 2s ease-in-out infinite",
  "&:hover": {
    transform: "translateX(-50%) scale(1.02)",
    boxShadow: "0 12px 32px rgba(59, 130, 246, 0.4)",
  },
  "@keyframes bounce": {
    "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
    "50%": { transform: "translateX(-50%) translateY(-10px)" },
  },
  "@media (min-width: 501px)": {
    maxWidth: 380,
    bottom: 160,
  },
}));

const DemoHintText = styled(Typography)(({ theme }) => ({
  fontSize: "0.9rem",
  color: "#1e40af",
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1.5,
  letterSpacing: "0.01em",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const LogoImage = styled("img")(() => ({
  height: 32,
  width: "auto",
  display: "block",
}));

const LikeCounter = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 999,
  backgroundColor: "rgba(255,255,255,0.18)",
  backdropFilter: "blur(8px)",
  fontSize: "0.75rem",
}));

const Content = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  paddingBottom: 0,
}));

const CardArea = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  marginBottom: 0,
}));

const LoadingCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(6),
  boxShadow: "none",
  border: "none",
  background: "transparent",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
}));

const Spinner = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: "999px",
  borderWidth: 4,
  borderStyle: "solid",
  borderColor: theme.palette.warning.main,
  borderTopColor: "transparent",
  margin: "0 auto",
  marginBottom: theme.spacing(1.5),
  animation: "spin 1s linear infinite",
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

const RestaurantCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "swipeDirection",
})(({ theme, swipeDirection }) => {
  let transform = "translateX(0) rotate(0deg)";
  let opacity = 1;

  if (swipeDirection === "left") {
    transform = "translateX(-120%) rotate(12deg)";
    opacity = 0;
  } else if (swipeDirection === "right") {
    transform = "translateX(120%) rotate(-12deg)";
    opacity = 0;
  }

  return {
    backgroundColor: theme.palette.common.white,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    transition: "transform 300ms ease, opacity 300ms ease",
    transform,
    opacity,
  };
});

const ImageWrapper = styled(Box)({
  position: "relative",
  height: 260,
  overflow: "hidden",
});

const RestaurantImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const ImageOverlay = styled(Box)({
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(to top, rgba(0,0,0,0.65), transparent, transparent)",
});

const CuisineTag = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 999,
  backgroundColor: "rgba(255,255,255,0.9)",
}));

const RatingPill = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 999,
  backgroundColor: "#FB923C",
  color: theme.palette.common.white,
  fontWeight: 700,
}));

const RestaurantContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDemoMode",
})(({ theme, isDemoMode }) => ({
  padding: theme.spacing(3),
  paddingBottom: isDemoMode ? theme.spacing(22) : theme.spacing(14),
}));

const SocialButton = styled("a")(({ theme }) => ({
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.shape.borderRadius * 2,
  color: "#fff",
  textDecoration: "none",
  boxShadow: theme.shadows[4],
  "&:hover": {
    boxShadow: theme.shadows[8],
  },
}));

const InstagramButton = styled(SocialButton)(({ theme }) => ({
  background: "linear-gradient(135deg, #EC4899 0%, #A855F7 50%, #F97316 100%)",
}));

const FacebookButton = styled(SocialButton)(({ theme }) => ({
  backgroundColor: "#2563EB",
}));

const TwitterButton = styled(SocialButton)(({ theme }) => ({
  backgroundColor: "#0EA5E9",
}));

const DescriptionWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})(({ expanded }) => ({
  position: "relative",
  overflow: "hidden",
  ...(expanded
    ? {}
    : {
        display: "-webkit-box",
        WebkitLineClamp: 4,
        WebkitBoxOrient: "vertical",
      }),
}));

const FadeOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})(({ expanded }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: expanded ? 0 : "2em",
  background: expanded
    ? "none"
    : "linear-gradient(to bottom, rgba(255,255,255,0), #fff)",
  pointerEvents: "none",
}));

const SeeMoreBtn = styled("span")(({ theme }) => ({
  display: "inline-block",
  color: theme.palette.primary.main,
  fontSize: "0.8rem",
  fontWeight: 600,
  cursor: "pointer",
  marginTop: theme.spacing(0.5),
}));

const DetailsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: theme.spacing(2),
}));

const DetailItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const FeatureChipsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(2),
}));

const FeatureChip = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1.25),
  borderRadius: 999,
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.secondary,
  fontSize: "0.7rem",
  fontWeight: 500,
}));

const PriceDollar = styled("span", {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  color: active ? theme.palette.success.main : theme.palette.grey[400],
  fontWeight: active ? 600 : 400,
  fontSize: "0.8rem",
}));

const ActionBar = styled(Box)(({ theme }) => ({
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  padding: theme.spacing(2.5, 3),
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.common.white,
  zIndex: theme.zIndex.appBar,

  "@media (min-width: 501px)": {
    position: "absolute",
    maxWidth: 440,
    left: "50%",
    transform: "translateX(-50%)",
    borderRadius: theme.spacing(0, 0, 2, 2),
  },
}));

const CircleButton = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  cursor: "pointer",
  transition: "transform 120ms ease, background-color 120ms ease",
  "&:active": { transform: "scale(0.95)" },
}));

const DislikeButton = styled(CircleButton)(({ theme }) => ({
  width: 64,
  height: 64,
  backgroundColor: "#fff",
  border: `4px solid #FCA5A5`,
  boxShadow: theme.shadows[4],
  "&:hover": { backgroundColor: "#FEF2F2" },
}));

const LikeButton = styled(CircleButton)(({ theme }) => ({
  width: 80,
  height: 80,
  background: "linear-gradient(135deg, #FB923C, #F97373)",
  boxShadow: theme.shadows[8],
}));

const SwipeWrapper = styled(Box)(() => ({
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  width: "100%",
  overflow: "hidden",
  margin: "0 auto",
  "@media (min-width: 501px)": {
    minWidth: 440,
    maxWidth: 440,
  },
}));

const PrimaryActionButton = styled(Button)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  fontWeight: 700,
  fontSize: "1rem",
  textTransform: "none",
  background: "linear-gradient(90deg, #F97316 0%, #EF4444 100%)",
  boxShadow: theme.shadows[6],
  color: "#fff",
  "&:hover": {
    boxShadow: theme.shadows[10],
    background: "linear-gradient(90deg, #FB923C 0%, #F97373 100%)",
  },
  marginTop: theme.spacing(6),
}));

/* ---------------------- HELPERS ---------------------- */

const metersToMiles = (meters) => {
  if (typeof meters !== "number") return "";
  const miles = meters * 0.000621371;
  if (miles < 0.1) return `${(miles * 5280).toFixed(0)} ft`;
  return `${miles.toFixed(1)} miles`;
};

const buildPhotoUrl = (photos) => {
  if (!photos || !photos.length) return null;
  const photo = photos[0];
  return `${photo.prefix}600x400${photo.suffix}`;
};

const normalizeHours = (hours) => {
  if (!hours) return null;
  if (Array.isArray(hours)) return hours[0] || null;
  return hours;
};

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return null;
  const normalized = timeStr.replace(":", "");
  if (normalized.length < 3) return null;
  const h = parseInt(normalized.slice(0, 2), 10);
  const m = parseInt(normalized.slice(2, 4) || "0", 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const formatTimeLabel = (timeStr) => {
  const minutes = parseTimeToMinutes(timeStr);
  if (minutes == null) return null;
  const now = new Date();
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const d = new Date(now);
  d.setHours(hours, mins, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const getTodayHoursLabel = (rawHours) => {
  const hours = normalizeHours(rawHours);
  if (!hours || !Array.isArray(hours.regular) || hours.regular.length === 0) {
    return null;
  }

  const now = new Date();
  const localDay = now.getDay();
  const fsqDay1to7 = localDay === 0 ? 7 : localDay;

  const todaysSlots = hours.regular.filter((slot) => {
    if (slot.day === undefined || slot.day === null) return false;
    return slot.day === fsqDay1to7 || slot.day === localDay;
  });

  const slot = todaysSlots[0] || hours.regular[0];
  if (!slot || !slot.open || !slot.close) return null;

  const openLabel = formatTimeLabel(slot.open);
  const closeLabel = formatTimeLabel(slot.close);
  if (!openLabel || !closeLabel) return null;

  return `${openLabel}–${closeLabel}`;
};

const extractFeatureChips = (features) => {
  if (!features) return [];
  const chips = [];

  const { amenities, services, food_and_drink, attributes } = features;

  if (amenities?.outdoor_seating) {
    chips.push({ key: "outdoor", label: "Outdoor seating" });
  }

  if (services?.delivery) {
    chips.push({ key: "delivery", label: "Delivery" });
  }

  if (
    services?.dine_in?.reservations ||
    services?.dine_in?.online_reservations
  ) {
    chips.push({ key: "reservations", label: "Takes reservations" });
  }

  if (food_and_drink?.alcohol) {
    chips.push({ key: "alcohol", label: "Serves alcohol" });
  }

  if (amenities?.live_music) {
    chips.push({ key: "music", label: "Live music" });
  }

  if (attributes?.good_for_dogs) {
    chips.push({ key: "dogs", label: "Dog friendly" });
  }

  return chips.slice(0, 4);
};

const PriceRange = ({ level = 0 }) => {
  const max = 4;
  return (
    <>
      {Array.from({ length: max }).map((_, idx) => (
        <PriceDollar key={idx} active={idx + 1 <= level}>
          $
        </PriceDollar>
      ))}
    </>
  );
};

const mapFsqPlaceToRestaurant = (place) => {
  const primaryCategory = place.categories?.[0];
  const imageUrl = buildPhotoUrl(place.photos) || RestaurantAltImage;

  const rating = place.rating ? (place.rating / 2).toFixed(1) : null;

  const priceLevel =
    typeof place.price === "number"
      ? Math.min(Math.max(place.price, 1), 4)
      : null;

  const hoursToday = getTodayHoursLabel(place.hours);
  const featureChips = extractFeatureChips(place.features);

  const social = place.social_media || {};

  return {
    id: place.fsq_id,
    name: place.name,
    cuisine: primaryCategory?.name || "Restaurant",
    image: imageUrl,
    distance: place.distance ? metersToMiles(place.distance) : "",
    priceLevel,
    rating,
    description:
      place.description ||
      place.location?.formatted_address ||
      primaryCategory?.name ||
      "",
    hoursToday,
    featureChips,
    website: place.website || "",
    social: {
      instagram: social.instagram || "",
      facebook: social.facebook || "",
      twitter: social.twitter || "",
    },
  };
};

/* ---------------------- COMPONENT ---------------------- */

const Swipe = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const [sessionInfo, setSessionInfo] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [likes, setLikes] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const descRef = useRef(null);

  // Demo mode tracking
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoHasFirstLike, setDemoHasFirstLike] = useState(false);
  const [demoVotesAfterFirstLike, setDemoVotesAfterFirstLike] = useState(0);
  const DEMO_VOTES_AFTER_MATCH = 5;

  // Swipe hint tracking (separate from demo mode)
  const [swipeHintDismissed, setSwipeHintDismissed] = useState(false);

  const [nextPageLink, setNextPageLink] = useState(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMoreResults, setHasMoreResults] = useState(true);

  // Track if match check is complete
  const [matchCheckComplete, setMatchCheckComplete] = useState(false);

  // Track initial load status
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [noResultsFound, setNoResultsFound] = useState(false);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  // CRITICAL: Prevent duplicate navigation
  const hasNavigatedToMatch = useRef(false);

  // CRITICAL: Prevent duplicate max-votes calls
  const hasCalledMaxVotes = useRef(false);

  // CRITICAL: Polling mechanism to check for matches
  const pollIntervalRef = useRef(null);
  const pollStartTimeRef = useRef(null);

  // CRITICAL: Helper function to handle match navigation
  const handleMatchFound = (source, restaurantName) => {
    if (hasNavigatedToMatch.current) {
      console.log(`[${source}] Match already handled, skipping navigation`);
      return;
    }

    hasNavigatedToMatch.current = true;
    console.log(`🎉 [${source}] MATCH FOUND! Restaurant: ${restaurantName}`);

    // Stop polling immediately
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      console.log("⏹️ [Polling] Stopped after match found");
    }

    posthog.capture("match_found", {
      session_id: sessionId,
      restaurant_name: restaurantName,
      source: source,
    });

    navigate(`/session/${sessionId}/your-restaurant`, { replace: true });
  };

  // CRITICAL: Poll for matches as a fallback
  const checkForMatch = async () => {
    if (hasNavigatedToMatch.current) return;

    try {
      console.log("🔍 [Polling] Checking for match...");
      const res = await authenticatedFetch(
        `${API_BASE_URL}/sessions/${sessionId}`,
        { method: "GET" }
      );

      if (!res.ok) {
        console.error("❌ [Polling] Error fetching session:", res.status);
        return;
      }

      const data = await res.json();
      const session = data.session;

      console.log("🔍 [Polling] Session status:", {
        hasMatch: session.matched_restaurant_id ? true : false,
        matchedRestaurantId: session.matched_restaurant_id,
        status: session.status,
      });

      // Check if there's a matched restaurant
      if (session.matched_restaurant_id || session.status === "matched") {
        console.log("🎉 [Polling] MATCH DETECTED!");
        handleMatchFound(
          "Polling",
          session.matched_restaurant_name || "Unknown"
        );
      }
    } catch (err) {
      console.error("❌ [Polling] Error checking for match:", err);
    }
  };

  // Detect demo mode when session loads
  useEffect(() => {
    if (sessionInfo) {
      const isDemo = sessionInfo.participantCount === 1;
      setIsDemoMode(isDemo);

      if (isDemo) {
        console.log(
          "🎮 [Demo Mode] Detected! First like will auto-match, then continue for 5 total likes"
        );
      }
    }
  }, [sessionInfo]);

  // Check if user has seen swipe hint (separate from demo mode)
  useEffect(() => {
    const hintSeen = getCookie("foodie_call_swipe_hint_seen");
    if (hintSeen === "true") {
      setSwipeHintDismissed(true);
    } else {
      // Auto-dismiss hint after 10 minutes for first-time users
      const timer = setTimeout(() => {
        setSwipeHintDismissed(true);
        setCookie("foodie_call_swipe_hint_seen", "true", 3650); // 10 years
      }, 600000); // 10 minutes

      return () => clearTimeout(timer);
    }
  }, []);

  // DEMO MODE: Navigate to match page after 5 votes AFTER first like
  useEffect(() => {
    if (
      isDemoMode &&
      demoHasFirstLike &&
      demoVotesAfterFirstLike >= DEMO_VOTES_AFTER_MATCH
    ) {
      console.log(
        `🎮 [Demo Mode] Completed ${DEMO_VOTES_AFTER_MATCH} votes after first like! Navigating to match page...`
      );

      posthog.capture("demo_completed", {
        session_id: sessionId,
        votes_after_match: demoVotesAfterFirstLike,
        total_likes: likes,
      });

      // Navigate to match page (the first like already created the match)
      navigate(`/session/${sessionId}/your-restaurant`, { replace: true });
    }
  }, [
    isDemoMode,
    demoHasFirstLike,
    demoVotesAfterFirstLike,
    DEMO_VOTES_AFTER_MATCH,
    navigate,
    sessionId,
    likes,
    posthog,
  ]);

  // CRITICAL: Start polling when user has liked at least one restaurant OR when they run out of restaurants
  // BUT NOT IN DEMO MODE (demo mode navigates automatically)
  useEffect(() => {
    // Skip polling in demo mode - we handle navigation ourselves
    if (isDemoMode) {
      return;
    }

    // Start polling if:
    // 1. User has liked at least one restaurant, OR
    // 2. User has run out of restaurants (might still get a match from others)
    const hasLikes = likes > 0;
    const hasRunOut =
      restaurants.length > 0 && currentIndex >= restaurants.length;

    if (!hasLikes && !hasRunOut) {
      // No likes yet and haven't run out, no need to poll
      return;
    }

    if (pollIntervalRef.current) {
      // Already polling
      return;
    }

    console.log("▶️ [Polling] Starting match polling (every 3 seconds)");
    console.log(
      "▶️ [Polling] Reason:",
      hasLikes ? "has likes" : "ran out of restaurants"
    );
    pollStartTimeRef.current = Date.now();

    // Start polling
    pollIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - pollStartTimeRef.current;

      if (elapsed > MATCH_POLL_TIMEOUT) {
        console.log("⏹️ [Polling] Stopping after timeout (5 minutes)");
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        return;
      }

      checkForMatch();
    }, MATCH_POLL_INTERVAL);

    // Check immediately
    checkForMatch();

    // Cleanup
    return () => {
      if (pollIntervalRef.current) {
        console.log("🧹 [Polling] Cleanup: Stopping poll interval");
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isDemoMode, likes, currentIndex, restaurants.length, sessionId]);

  // Call max-votes endpoint when user exhausts restaurants (NOT in demo mode)
  useEffect(() => {
    if (isDemoMode) {
      // Skip max-votes in demo mode
      return;
    }

    if (
      restaurants.length > 0 &&
      currentIndex >= restaurants.length &&
      !hasCalledMaxVotes.current
    ) {
      hasCalledMaxVotes.current = true;

      console.log(
        "🏁 User ran out of restaurants, calling max-votes endpoint..."
      );

      (async () => {
        try {
          const res = await authenticatedFetch(
            `${API_BASE_URL}/sessions/${sessionId}/max-votes`,
            {
              method: "POST",
            }
          );

          const data = await res.json().catch(() => ({}));
          console.log("📡 max-votes response:", data);

          if (!res.ok) {
            console.error("❌ Error calling max-votes:", data);
          } else {
            console.log("✅ max-votes call successful:", data.message);
          }
        } catch (err) {
          console.error("❌ Network error calling max-votes:", err);
        }
      })();
    }
  }, [isDemoMode, currentIndex, restaurants.length, sessionId]);

  useEffect(() => {
    posthog.capture("swipe_page_viewed", {
      session_id: sessionId,
    });
  }, [sessionId, posthog]);

  useEffect(() => {
    if (restaurants.length > 0) {
      posthog.capture("restaurants_loaded", {
        session_id: sessionId,
        count: restaurants.length,
      });
    }
  }, [restaurants, sessionId, posthog]);

  useEffect(() => {
    const currentRestaurant =
      restaurants.length > 0 ? restaurants[currentIndex] : null;
    if (currentRestaurant) {
      posthog.capture("restaurant_viewed", {
        session_id: sessionId,
        restaurant_name: currentRestaurant.name,
        cuisine: currentRestaurant.cuisine,
        price_level: currentRestaurant.priceLevel,
        rating: currentRestaurant.rating,
        distance: currentRestaurant.distance,
      });
    }
  }, [currentIndex, restaurants, sessionId, posthog]);

  useEffect(() => {
    return () => {
      if (likes > 0) {
        posthog.capture("session_abandoned", {
          session_id: sessionId,
          total_swipes: currentIndex,
          total_likes: likes,
        });
      }
    };
  }, [sessionId, likes, currentIndex, posthog]);

  /* ---------- 1) Fetch session info ---------- */
  useEffect(() => {
    if (!sessionId) return;

    const fetchSession = async () => {
      try {
        const res = await authenticatedFetch(
          `${API_BASE_URL}/sessions/${sessionId}`,
          {
            method: "GET",
          }
        );

        if (!res.ok) {
          const text = await res.text();
          console.error(
            "Error fetching session by id:",
            res.status,
            res.statusText,
            text
          );
          return;
        }

        const data = await res.json();
        console.log("Loaded sessionInfo from API:", data);
        setSessionInfo(data.session);
      } catch (err) {
        console.error("Failed to fetch session by id:", err);
      }
    };

    fetchSession();
  }, [sessionId]);

  /* ---------- 2) Fetch restaurants ---------- */
  const fetchRestaurants = async (isInitialLoad = true, pageLink = null) => {
    if (!sessionInfo || !FOURSQUARE_API_KEY) {
      if (!sessionInfo) console.warn("No sessionInfo loaded yet");
      if (!FOURSQUARE_API_KEY) console.warn("Missing Foursquare API key");
      return;
    }

    if (!isInitialLoad && restaurants.length >= MAX_RESTAURANTS) {
      console.log(`Reached maximum restaurant limit (${MAX_RESTAURANTS})`);
      setHasMoreResults(false);
      return;
    }

    if (!isInitialLoad && (isFetchingMore || !hasMoreResults)) {
      return;
    }

    if (!isInitialLoad) {
      setIsFetchingMore(true);
    }

    try {
      let url;

      if (pageLink) {
        url = pageLink;
        console.log("Fetching next page from link:", url);
      } else {
        const {
          ll,
          radius,
          fsq_category_ids,
          exclude_all_chains,
          min_price,
          max_price,
          open_now,
          session_token,
          sort_by,
        } = sessionInfo;

        const params = new URLSearchParams();

        if (open_now === true) {
          params.append("open_now", "true");
        }
        if (sort_by === "rating") {
          params.append("sort", "RATING");
        } else if (sort_by === "popularity") {
          params.append("sort", "POPULARITY");
        } else {
          params.append("sort", "DISTANCE");
        }
        if (ll) params.append("ll", ll);
        if (radius) params.append("radius", radius);
        if (fsq_category_ids && fsq_category_ids.length > 0) {
          params.append("categories", fsq_category_ids.join(","));
        } else {
          params.append("query", "food");
        }
        if (exclude_all_chains) params.append("exclude_all_chains", "true");

        if (min_price != null && typeof min_price === "number") {
          params.append("min_price", String(min_price));
        }
        if (max_price != null && typeof max_price === "number") {
          params.append("max_price", String(max_price));
        }

        if (open_now) params.append("open_now", "true");

        params.append("fields", FOURSQUARE_FIELDS);
        params.append("limit", "50");

        if (session_token) {
          params.append("session_token", session_token);
        }

        url = `${FOURSQUARE_BASE_URL}?${params.toString()}`;
      }

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${FOURSQUARE_API_KEY}`,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        console.error(
          "Foursquare error response:",
          res.status,
          res.statusText,
          "Body:",
          text
        );
        setHasMoreResults(false);
        return;
      }

      const data = await res.json();

      const linkHeader = res.headers.get("Link");
      if (linkHeader) {
        const nextLinkMatch = linkHeader.match(/<([^>]+)>;\s*rel="next"/);
        if (nextLinkMatch) {
          setNextPageLink(nextLinkMatch[1]);
          console.log("Next page link found:", nextLinkMatch[1]);
        } else {
          setNextPageLink(null);
          setHasMoreResults(false);
        }
      } else {
        setNextPageLink(null);
        setHasMoreResults(false);
      }

      const mapped =
        data?.results?.map((place) => mapFsqPlaceToRestaurant(place)) ?? [];

      if (isInitialLoad) {
        setRestaurants(mapped);
        setCurrentIndex(0);
        setInitialLoadComplete(true);

        // Check if we got zero results
        if (mapped.length === 0) {
          setNoResultsFound(true);
        }
      } else {
        const remainingSlots = MAX_RESTAURANTS - restaurants.length;
        const restaurantsToAdd = mapped.slice(0, remainingSlots);

        setRestaurants((prev) => [...prev, ...restaurantsToAdd]);
        console.log(
          `Loaded ${restaurantsToAdd.length} more restaurants. Total: ${
            restaurants.length + restaurantsToAdd.length
          }`
        );

        if (restaurants.length + restaurantsToAdd.length >= MAX_RESTAURANTS) {
          console.log(`Reached maximum restaurant limit (${MAX_RESTAURANTS})`);
          setHasMoreResults(false);
          setNextPageLink(null);
        }
      }

      if (mapped.length < 50) {
        setHasMoreResults(false);
      }
    } catch (err) {
      console.error("Error calling Foursquare Places API:", err);
      setHasMoreResults(false);
    } finally {
      if (!isInitialLoad) {
        setIsFetchingMore(false);
      }
    }
  };

  /* ---------- 3) Initial load ---------- */
  useEffect(() => {
    if (sessionInfo) {
      fetchRestaurants(true);
    }
  }, [sessionInfo]);

  /* ---------- 4) Auto-fetch more ---------- */
  useEffect(() => {
    const threshold = 10;
    const shouldFetchMore =
      restaurants.length > 0 &&
      currentIndex >= restaurants.length - threshold &&
      hasMoreResults &&
      !isFetchingMore &&
      nextPageLink;

    if (shouldFetchMore) {
      console.log(
        `Approaching end of restaurants (${currentIndex + 1}/${
          restaurants.length
        }). Fetching more...`
      );
      fetchRestaurants(false, nextPageLink);
    }
  }, [
    currentIndex,
    restaurants.length,
    hasMoreResults,
    isFetchingMore,
    nextPageLink,
  ]);

  /* ---------- 5) WEBSOCKET WITH COMPREHENSIVE DEBUGGING ---------- */
  useEffect(() => {
    // Skip WebSocket in demo mode - we don't need it
    if (isDemoMode) {
      console.log("🎮 [Demo Mode] Skipping WebSocket setup");
      return;
    }

    if (!sessionId) {
      console.warn("Swipe: No sessionId provided — skipping WebSocket setup.");
      return;
    }

    let isMounted = true;

    const connectWebSocket = () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      console.log(
        "🔌 [WebSocket] Opening authenticated WebSocket for sessionId:",
        sessionId
      );

      const ws = new WebSocket(getAuthenticatedWSUrl(sessionId));
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("✅ [WebSocket] Connected successfully");
        reconnectAttemptsRef.current = 0;

        const subscribeMsg = {
          action: "subscribeSession",
          sessionId,
        };

        ws.send(JSON.stringify(subscribeMsg));
        console.log(
          "📤 [WebSocket] Sent subscribeSession for sessionId:",
          sessionId
        );
      };

      ws.onmessage = (event) => {
        console.log("📨 [WebSocket] Raw message received:", event.data);

        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch (e) {
          console.error("❌ [WebSocket] Failed to parse message:", e);
          return;
        }

        console.log("📨 [WebSocket] Parsed message:", msg);
        console.log("📨 [WebSocket] Message type:", msg.type);
        console.log(
          "📨 [WebSocket] Message data:",
          JSON.stringify(msg, null, 2)
        );

        // Check for match in multiple possible formats
        const isMatchMessage =
          msg.type === "MATCH_FOUND" ||
          msg.type === "match" ||
          msg.type === "MATCH" ||
          msg.message === "MATCH_FOUND" ||
          msg.message === "match" ||
          (msg.data &&
            (msg.data.type === "MATCH_FOUND" || msg.data.type === "match"));

        if (isMatchMessage && isMounted) {
          const restaurantName =
            msg.restaurantName ||
            msg.restaurant_name ||
            msg.data?.restaurantName ||
            msg.data?.restaurant_name ||
            "Unknown";
          console.log("🎉 [WebSocket] MATCH MESSAGE DETECTED!");
          handleMatchFound("WebSocket", restaurantName);
        } else {
          console.log("ℹ️ [WebSocket] Non-match message received:", msg.type);
        }

        // Check for NO_MATCH_POSSIBLE message
        const isNoMatchMessage =
          msg.type === "NO_MATCH_POSSIBLE" ||
          msg.message === "NO_MATCH_POSSIBLE";

        if (isNoMatchMessage && isMounted) {
          console.log("❌ [WebSocket] NO_MATCH_POSSIBLE received");
          // Lambda already did a 3-second delay before sending this, so we can trust it
          // Just do a quick check to make sure we haven't navigated
          if (!hasNavigatedToMatch.current) {
            console.log("✅ Setting matchCheckComplete - no match found");
            setMatchCheckComplete(true);
          } else {
            console.log(
              "⚠️ Match navigation occurred, ignoring NO_MATCH_POSSIBLE"
            );
          }
        }
      };

      ws.onerror = (err) => {
        console.error("❌ [WebSocket] Error:", err);
      };

      ws.onclose = (event) => {
        console.log(
          "🔌 [WebSocket] Closed:",
          event.code,
          event.reason || "(no reason)"
        );

        if (isMounted && event.code !== 1000 && event.code !== 1001) {
          if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
            reconnectAttemptsRef.current += 1;
            const delay = Math.min(
              1000 * 2 ** reconnectAttemptsRef.current,
              30000
            );
            console.log(
              `🔄 [WebSocket] Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`
            );

            reconnectTimeoutRef.current = setTimeout(() => {
              if (isMounted) {
                connectWebSocket();
              }
            }, delay);
          } else {
            console.error(
              "❌ [WebSocket] Max reconnection attempts reached. Relying on polling fallback."
            );
          }
        }
      };
    };

    connectWebSocket();

    return () => {
      console.log("🧹 [WebSocket] Cleanup: Closing WebSocket");
      isMounted = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isDemoMode, sessionId, navigate]);

  useEffect(() => {
    setDescExpanded(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentIndex]);

  useEffect(() => {
    if (!descRef.current) return;
    if (descExpanded) return;

    const el = descRef.current;
    const isOverflowing = el.scrollHeight - el.clientHeight > 1;
    setCanExpand(isOverflowing);
  }, [restaurants, currentIndex, descExpanded]);

  const currentRestaurant =
    restaurants.length > 0 ? restaurants[currentIndex] : null;

  const handleSwipe = (direction) => {
    if (!restaurants.length) return;

    const currentRestaurant = restaurants[currentIndex];
    const fsq_id = currentRestaurant?.id;

    setSwipeDirection(direction);

    // Dismiss swipe hint on first swipe and save cookie
    if (!swipeHintDismissed) {
      setSwipeHintDismissed(true);
      setCookie("foodie_call_swipe_hint_seen", "true", 3650); // 10 years
    }

    const advanceCard = () => {
      setSwipeDirection(null);
      setCurrentIndex((i) => i + 1);
    };

    // DEMO MODE: Count votes AFTER first like
    if (isDemoMode && demoHasFirstLike) {
      setDemoVotesAfterFirstLike((count) => count + 1);
      console.log(
        `🎮 [Demo Mode] Vote ${
          demoVotesAfterFirstLike + 1
        }/${DEMO_VOTES_AFTER_MATCH} after first like`
      );
    }

    posthog.capture(direction === "left" ? "swipe_left" : "swipe_right", {
      session_id: sessionId,
      restaurant_name: currentRestaurant.name,
      cuisine: currentRestaurant.cuisine,
      price_level: currentRestaurant.priceLevel,
      rating: currentRestaurant.rating,
      card_position: currentIndex + 1,
      total_likes: direction === "right" ? likes + 1 : likes,
      is_demo: isDemoMode,
    });

    if (direction === "left") {
      setTimeout(advanceCard, 300);
      return;
    }

    if (direction === "right") {
      setLikes((m) => m + 1);

      // DEMO MODE: Only send the FIRST like to backend
      if (isDemoMode) {
        if (!demoHasFirstLike) {
          // This is the first like - send it to backend to create match
          setDemoHasFirstLike(true);
          console.log(
            "🎮 [Demo Mode] First like! Sending to backend to create match..."
          );

          (async () => {
            try {
              console.log(
                `🍔 [API - Demo] Sending FIRST like for restaurant: ${currentRestaurant.name} (${fsq_id})`
              );

              const res = await authenticatedFetch(
                `${API_BASE_URL}/sessions/${sessionId}/like`,
                {
                  method: "POST",
                  body: JSON.stringify({ fsq_id }),
                }
              );

              console.log(
                `📡 [API - Demo] Like response status: ${res.status}`
              );

              const data = await res.json().catch(() => ({}));
              console.log("📡 [API - Demo] Like response data:", data);

              if (!res.ok) {
                console.error("❌ [API - Demo] Error recording like:", data);
              } else {
                console.log(
                  "✅ [API - Demo] First like recorded - match created!"
                );
              }
            } catch (err) {
              console.error(
                "❌ [API - Demo] Network error recording like:",
                err
              );
            }
          })();
        } else {
          // Subsequent likes in demo mode - don't send to backend
          console.log(
            `🎮 [Demo Mode] Like ${
              likes + 1
            } - NOT sending to backend (demo only)`
          );
        }
      } else {
        // NORMAL MODE: Send every like to backend
        (async () => {
          try {
            console.log(
              `🍔 [API] Sending like for restaurant: ${currentRestaurant.name} (${fsq_id})`
            );

            const res = await authenticatedFetch(
              `${API_BASE_URL}/sessions/${sessionId}/like`,
              {
                method: "POST",
                body: JSON.stringify({ fsq_id }),
              }
            );

            console.log(`📡 [API] Like response status: ${res.status}`);

            const data = await res.json().catch(() => ({}));
            console.log("📡 [API] Like response data:", data);

            if (!res.ok) {
              console.error("❌ [API] Error recording like:", data);
            } else {
              console.log("✅ [API] Like recorded successfully");

              if (data.isMatch || data.match || data.is_match) {
                console.log("🎉 [API] Match detected in response!");
                handleMatchFound("API", currentRestaurant.name);
              } else {
                console.log(
                  "ℹ️ [API] No match yet. Polling will continue checking."
                );
              }
            }
          } catch (err) {
            console.error("❌ [API] Network error recording like:", err);
          }
        })();
      }

      setTimeout(advanceCard, 300);
    }
  };

  const handleStartOver = () => {
    navigate("/setup");

    posthog.capture("start_over_from_match_page", {
      session_id: sessionId,
    });
  };

  return (
    <SwipeWrapper>
      <Content>
        <Box>
          {/* Demo Mode Banner */}
          {isDemoMode && (
            <DemoBanner>
              <DemoBannerText>
                <Eye size={16} />
                DEMO MODE
              </DemoBannerText>
            </DemoBanner>
          )}

          <Header>
            <HeaderTopRow>
              <Box display="flex" flexDirection="column">
                <LogoImage src={FoodieCallAltLogo} alt="Foodie Call Alt Logo" />
                <Typography
                  variant="body2"
                  sx={{ color: "orange.50", mt: 0.25 }}
                >
                  Find your next meal together
                </Typography>
              </Box>

              <LikeCounter>
                <Heart size={14} fill="white" />
                <Typography variant="caption" fontWeight={500}>
                  {likes} likes
                </Typography>
              </LikeCounter>
            </HeaderTopRow>
          </Header>

          <CardArea>
            {currentRestaurant ? (
              <RestaurantCard swipeDirection={swipeDirection}>
                <ImageWrapper>
                  <RestaurantImage
                    src={currentRestaurant.image}
                    alt={RestaurantAltImage}
                  />
                  <ImageOverlay />
                  <CuisineTag>
                    <Typography variant="caption" fontWeight={500}>
                      {currentRestaurant.cuisine}
                    </Typography>
                  </CuisineTag>
                  {currentRestaurant.rating && (
                    <RatingPill>⭐ {currentRestaurant.rating}</RatingPill>
                  )}
                </ImageWrapper>

                <RestaurantContent isDemoMode={isDemoMode}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ flex: 1, pr: 2 }}
                    >
                      {currentRestaurant.name}
                    </Typography>

                    {(currentRestaurant.social?.instagram ||
                      currentRestaurant.social?.facebook ||
                      currentRestaurant.social?.twitter) && (
                      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                        {currentRestaurant.social.instagram && (
                          <InstagramButton
                            href={`https://instagram.com/${currentRestaurant.social.instagram.replace(
                              "@",
                              ""
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              posthog.capture(
                                "social_media_clicked_from_swipe",
                                {
                                  session_id: sessionId,
                                  restaurant_name: currentRestaurant.name,
                                  platform: "instagram",
                                }
                              );
                            }}
                          >
                            <Instagram size={18} />
                          </InstagramButton>
                        )}

                        {currentRestaurant.social.facebook && (
                          <FacebookButton
                            href={`https://facebook.com/${currentRestaurant.social.facebook}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              posthog.capture(
                                "social_media_clicked_from_swipe",
                                {
                                  session_id: sessionId,
                                  restaurant_name: currentRestaurant.name,
                                  platform: "facebook",
                                }
                              );
                            }}
                          >
                            <Facebook size={18} />
                          </FacebookButton>
                        )}

                        {currentRestaurant.social.twitter && (
                          <TwitterButton
                            href={`https://twitter.com/${currentRestaurant.social.twitter.replace(
                              "@",
                              ""
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              posthog.capture(
                                "social_media_clicked_from_swipe",
                                {
                                  session_id: sessionId,
                                  restaurant_name: currentRestaurant.name,
                                  platform: "twitter",
                                }
                              );
                            }}
                          >
                            <Twitter size={18} />
                          </TwitterButton>
                        )}
                      </Stack>
                    )}
                  </Box>

                  {currentRestaurant.description && (
                    <Box sx={{ mb: 2 }}>
                      <DescriptionWrapper expanded={descExpanded} ref={descRef}>
                        <Typography variant="body2">
                          {currentRestaurant.description}
                        </Typography>

                        {canExpand && !descExpanded && (
                          <FadeOverlay expanded={false} />
                        )}
                      </DescriptionWrapper>

                      {canExpand && (
                        <SeeMoreBtn
                          onClick={() => setDescExpanded((prev) => !prev)}
                        >
                          {descExpanded ? "See less" : "See more"}
                        </SeeMoreBtn>
                      )}
                    </Box>
                  )}

                  <DetailsGrid>
                    {currentRestaurant.distance && (
                      <DetailItem>
                        <MapPin size={18} />
                        <Typography variant="caption">
                          {currentRestaurant.distance}
                        </Typography>
                      </DetailItem>
                    )}

                    {typeof currentRestaurant.priceLevel === "number" && (
                      <DetailItem>
                        <DollarSign size={18} />
                        <Typography variant="caption">
                          <PriceRange level={currentRestaurant.priceLevel} />
                        </Typography>
                      </DetailItem>
                    )}

                    {currentRestaurant.hoursToday && (
                      <DetailItem>
                        <Clock size={18} />
                        <Typography variant="caption">
                          {currentRestaurant.hoursToday}
                        </Typography>
                      </DetailItem>
                    )}

                    {currentRestaurant.website && (
                      <DetailItem
                        sx={{
                          cursor: "pointer",
                          "&:hover": { color: "warning.main" },
                          transition: "color 0.2s ease",
                        }}
                        onClick={() => {
                          const url = currentRestaurant.website.startsWith(
                            "http"
                          )
                            ? currentRestaurant.website
                            : `https://${currentRestaurant.website}`;
                          window.open(url, "_blank", "noopener,noreferrer");

                          posthog.capture("website_clicked_from_swipe", {
                            session_id: sessionId,
                            restaurant_name: currentRestaurant.name,
                          });
                        }}
                      >
                        <Globe size={18} />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          Website
                          <ExternalLink size={12} />
                        </Typography>
                      </DetailItem>
                    )}
                  </DetailsGrid>

                  {currentRestaurant.featureChips &&
                    currentRestaurant.featureChips.length > 0 && (
                      <FeatureChipsRow>
                        {currentRestaurant.featureChips.map((chip) => (
                          <FeatureChip key={chip.key}>{chip.label}</FeatureChip>
                        ))}
                      </FeatureChipsRow>
                    )}
                </RestaurantContent>
              </RestaurantCard>
            ) : restaurants.length > 0 && currentIndex >= restaurants.length ? (
              <LoadingCard>
                <Box
                  component="img"
                  src={matchCheckComplete ? SadPizzaImage : HangTightPizza}
                  alt="No more restaurants"
                  sx={{
                    height: 120,
                    objectFit: "contain",
                    margin: "0 auto",
                    mb: 2,
                  }}
                />
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  {matchCheckComplete
                    ? "No Match Found"
                    : restaurants.length >= MAX_RESTAURANTS
                    ? "You've reached the limit!"
                    : "No more restaurants"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {matchCheckComplete
                    ? "Unfortunately, you and your partner(s) didn't match on any restaurants. Try adjusting your preferences and starting a new session!"
                    : restaurants.length >= MAX_RESTAURANTS
                    ? `You've viewed ${MAX_RESTAURANTS} restaurants in your area.`
                    : "You've seen all the available restaurants in your area that match your session preferences."}
                </Typography>

                {/* Waiting Spinner and Status - Only show when NOT complete */}
                {!matchCheckComplete && (
                  <Box sx={{ mt: 3, mb: 2 }}>
                    <Spinner />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mt: 2, mb: 0.5 }}
                    >
                      Waiting for your partner(s) to finish voting...
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      A match could still be coming! Don't leave yet.
                    </Typography>
                  </Box>
                )}

                {/* Button - Only show when match check is complete */}
                {matchCheckComplete && (
                  <PrimaryActionButton onClick={handleStartOver} sx={{ mt: 3 }}>
                    Back to the Kitchen!
                  </PrimaryActionButton>
                )}
              </LoadingCard>
            ) : (
              <LoadingCard>
                {!initialLoadComplete || !noResultsFound ? (
                  <>
                    <Spinner />
                    <Typography variant="body2">
                      {!sessionInfo
                        ? "Loading your session settings..."
                        : FOURSQUARE_API_KEY
                        ? "Looking for restaurants nearby..."
                        : "Add your Foursquare API key to start loading restaurants."}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box
                      component="img"
                      src={SadPizzaImage}
                      alt="No restaurants found"
                      sx={{
                        height: 120,
                        objectFit: "contain",
                        margin: "0 auto",
                        mb: 2,
                      }}
                    />
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                      No Restaurants Found
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      We couldn't find any restaurants matching your current
                      preferences. Try expanding your search by:
                    </Typography>
                    <Box sx={{ textAlign: "left", mb: 3, px: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        • Increasing your search radius
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        • Removing price restrictions
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        • Selecting different cuisine types
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Turning off "Open Now" filter
                      </Typography>
                    </Box>
                    <PrimaryActionButton
                      onClick={handleStartOver}
                      sx={{ mt: 1 }}
                    >
                      Adjust Preferences
                    </PrimaryActionButton>
                  </>
                )}
              </LoadingCard>
            )}

            {isFetchingMore && (
              <Box sx={{ textAlign: "center", mt: 2, opacity: 0.7 }}>
                <Typography variant="caption">
                  Loading more restaurants...
                </Typography>
              </Box>
            )}
          </CardArea>
        </Box>
      </Content>

      {sessionInfo && currentRestaurant && (
        <>
          {/* Swipe Hint - Show for all first-time users */}
          {!swipeHintDismissed && (
            <DemoHint
              onClick={() => {
                setSwipeHintDismissed(true);
                setCookie("foodie_call_swipe_hint_seen", "true", 3650);
              }}
            >
              <DemoHintText>
                👇 Tap ❌ to pass or ❤️ to like this restaurant!
              </DemoHintText>
            </DemoHint>
          )}

          <ActionBar>
            <Stack direction="row" justifyContent="center" spacing={3}>
              <DislikeButton onClick={() => handleSwipe("left")}>
                <X size={32} color="#EF4444" />
              </DislikeButton>

              <LikeButton onClick={() => handleSwipe("right")}>
                <Heart size={36} color="#fff" fill="#fff" />
              </LikeButton>
            </Stack>
          </ActionBar>
        </>
      )}
    </SwipeWrapper>
  );
};

export default Swipe;
